import React, { useState, useEffect } from 'react';
import { Button, Tag, ProgressBar } from 'antd-mobile';
import { PlayOutline, ClockCircleOutline, UserOutline, TeamOutline } from 'antd-mobile-icons';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import TextLightScroll from '../../components/texts/TextLightScroll';

const InteractiveLearningList = () => {
  const navigate = useNavigate();

  const interactiveCourses = [
    {
      id: 1,
      title: 'AI编程实战训练营',
      description: '通过实际项目练习，掌握AI开发的核心技能，包括机器学习模型训练、部署等',
      type: '编程实战',
      participants: 156,
      duration: '4周',
      difficulty: '中级',
      progress: 65,
      instructor: 'AI导师团队',
      thumbnail: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=300&h=200&fit=crop',
      tags: ['Python', 'AI', '实战项目'],
      features: ['实时代码评审', '1对1指导', '项目作品集']
    },
    {
      id: 2,
      title: '数据分析挑战赛',
      description: '基于真实商业数据，进行数据挖掘和分析，提升数据洞察能力',
      type: '竞赛挑战',
      participants: 89,
      duration: '2周',
      difficulty: '高级',
      progress: 0,
      instructor: '数据科学专家',
      thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=300&h=200&fit=crop',
      tags: ['数据分析', 'SQL', 'Python'],
      features: ['排行榜竞赛', '奖金激励', '企业认证']
    },
    {
      id: 3,
      title: 'Web开发协作项目',
      description: '团队协作开发完整的Web应用，体验真实的软件开发流程',
      type: '团队协作',
      participants: 234,
      duration: '6周',
      difficulty: '中级',
      progress: 30,
      instructor: '全栈开发团队',
      thumbnail: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=300&h=200&fit=crop',
      tags: ['React', 'Node.js', '团队协作'],
      features: ['敏捷开发', '代码审查', '产品发布']
    },
    {
      id: 4,
      title: '区块链技术探索',
      description: '深入了解区块链技术原理，开发智能合约和DApp应用',
      type: '技术探索',
      participants: 67,
      duration: '3周',
      difficulty: '高级',
      progress: 0,
      instructor: '区块链专家',
      thumbnail: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=300&h=200&fit=crop',
      tags: ['区块链', 'Solidity', 'Web3'],
      features: ['实验环境', '专家答疑', '技术社区']
    }
  ];

  const handleCourseClick = (courseId: number) => {
    navigate(`/learn/interactive/${courseId}`);
  };

  const getTypeColor = (type: string) => {
    const colors: { [key: string]: string } = {
      '编程实战': '#4CAF50',
      '竞赛挑战': '#FF9800',
      '团队协作': '#2196F3',
      '技术探索': '#9C27B0'
    };
    return colors[type] || '#666';
  };

  const getDifficultyColor = (difficulty: string) => {
    const colors: { [key: string]: string } = {
      '初级': '#4CAF50',
      '中级': '#FF9800',
      '高级': '#F44336'
    };
    return colors[difficulty] || '#666';
  };

  return (
    <StyledWrapper>
      <div className="interactive-list-container">
        <div className="header">
          <TextLightScroll text="互动学习" />
          <p>参与实战项目，在协作中提升技能</p>
        </div>

        <div className="filter-section">
          <div className="filter-tags">
            <Tag color="primary">全部</Tag>
            <Tag>编程实战</Tag>
            <Tag>竞赛挑战</Tag>
            <Tag>团队协作</Tag>
            <Tag>技术探索</Tag>
          </div>
        </div>

        <div className="courses-grid">
          {interactiveCourses.map((course) => (
            <div key={course.id} className="course-card" onClick={() => handleCourseClick(course.id)}>
              <div className="course-thumbnail">
                <img src={course.thumbnail} alt={course.title} />
                <div className="course-overlay">
                  <div className="play-button">
                    <PlayOutline />
                  </div>
                  <div className="type-badge" style={{ backgroundColor: getTypeColor(course.type) }}>
                    {course.type}
                  </div>
                  <div className="difficulty-badge" style={{ backgroundColor: getDifficultyColor(course.difficulty) }}>
                    {course.difficulty}
                  </div>
                </div>
              </div>
              <div className="course-content">
                <div className="course-header">
                  <h3>{course.title}</h3>
                </div>
                <p className="description">{course.description}</p>
                
                <div className="course-meta">
                  <div className="meta-item">
                    <ClockCircleOutline />
                    <span>{course.duration}</span>
                  </div>
                  <div className="meta-item">
                    <TeamOutline />
                    <span>{course.participants}人参与</span>
                  </div>
                </div>

                <div className="course-tags">
                  {course.tags.map((tag, index) => (
                    <span key={index} className="tag">{tag}</span>
                  ))}
                </div>

                {course.progress > 0 && (
                  <div className="progress-section">
                    <div className="progress-header">
                      <span className="progress-label">进度</span>
                      <span className="progress-value">{course.progress}%</span>
                    </div>
                    <ProgressBar percent={course.progress} />
                  </div>
                )}

                <div className="course-footer">
                  <div className="participants">导师：{course.instructor}</div>
                  <div className="status">{course.progress > 0 ? '进行中' : '可参与'}</div>
                </div>
                
                <button 
                   className="join-btn"
                   onClick={(e) => {
                     e.stopPropagation();
                     handleCourseClick(course.id);
                   }}
                 >
                   {course.progress > 0 ? '继续学习' : '立即参与'}
                 </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  .interactive-list-container {
    padding: 40px 24px;
    max-width: 1200px;
    margin: 0 auto;
    color: #1f2937;
  }

  .header {
    text-align: center;
    margin-bottom: 40px;
    position: relative;
    
    p {
      font-size: 1.2rem;
      opacity: 0.9;
      margin: 0;
      padding-top: 80px;
    }
  }

  .filter-section {
    margin-bottom: 40px;
    
    .filter-tags {
      display: flex;
      gap: 12px;
      flex-wrap: wrap;
      justify-content: center;
    }
  }

  .courses-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 24px;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 20px;
    }
  }

  .course-card {
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 20px;
    overflow: hidden;
    transition: all 0.3s ease;
    cursor: pointer;
    position: relative;
    
    &:hover {
      transform: translateY(-8px) scale(1.02);
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    }
  }

  .course-thumbnail {
    position: relative;
    height: 200px;
    overflow: hidden;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    }
    
    &:hover img {
      transform: scale(1.1);
    }
  }

  .course-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.7) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: all 0.3s ease;
    
    .play-button {
      background: rgba(255, 255, 255, 0.9);
      border-radius: 50%;
      width: 60px;
      height: 60px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #1f2937;
      font-size: 24px;
      transform: scale(0.8);
      transition: all 0.3s ease;
    }
    
    .type-badge {
      position: absolute;
      top: 16px;
      left: 16px;
      color: white;
      padding: 6px 12px;
      border-radius: 12px;
      font-size: 0.8rem;
      font-weight: 600;
      backdrop-filter: blur(10px);
    }
    
    .difficulty-badge {
      position: absolute;
      top: 16px;
      right: 16px;
      color: white;
      padding: 6px 12px;
      border-radius: 12px;
      font-size: 0.8rem;
      font-weight: 600;
      backdrop-filter: blur(10px);
    }
  }

  .course-card:hover .course-overlay {
    opacity: 1;
    
    .play-button {
      transform: scale(1);
    }
  }

  .course-content {
    padding: 24px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
  }

  .course-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
    
    h3 {
      font-size: 1.2rem;
      font-weight: 700;
      margin: 0;
      color: #1f2937;
      flex: 1;
      line-height: 1.4;
    }
  }

  .description {
    font-size: 0.9rem;
    color: #6b7280;
    margin: 12px 0 16px 0;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .course-meta {
    display: flex;
    gap: 16px;
    margin: 16px 0;
    
    .meta-item {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 0.85rem;
      color: #6b7280;
    }
  }

  .progress-section {
    margin: 16px 0;
    
    .progress-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
      
      .progress-label {
        font-size: 0.85rem;
        color: #6b7280;
      }
      
      .progress-value {
        font-size: 0.85rem;
        font-weight: 600;
        color: #6366f1;
      }
    }
    
    .adm-progress-bar {
      --fill-color: #6366f1;
      --track-color: rgba(99, 102, 241, 0.1);
    }
  }

  .course-tags {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin: 16px 0;
    
    .tag {
      background: rgba(99, 102, 241, 0.1);
      color: #6366f1;
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 0.75rem;
      font-weight: 500;
    }
  }

  .course-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 20px 0 16px 0;
    padding-top: 16px;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    
    .participants {
      font-size: 0.85rem;
      color: #6b7280;
    }
    
    .status {
      font-size: 0.85rem;
      font-weight: 600;
      color: #10b981;
      background: rgba(16, 185, 129, 0.1);
      padding: 4px 8px;
      border-radius: 8px;
    }
  }

  .join-btn {
    width: 100%;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 12px;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
    }
    
    &:active {
      transform: translateY(0);
    }
  }
`;

export default InteractiveLearningList;